import React from 'react';
import { Camera, ChevronDown, ChevronRight, ImagePlus, Pencil, Trash2, X } from 'lucide-react';
import {
  ACK_COPY, PASTE_COPY, pickerCategories, asksFollowUps, buildPayload, canSend, categoryCopy, examplePrompt,
  cleanContext, type FeedbackDraft, type FlowAction, type FlowState, type Shot,
} from '@/lib/feedback/core';
import { submitReport, type FeedbackConfig, type MyReport, type SubmitResult } from '@/lib/feedback/api';
import { currentWhere, gatherFeedbackContext, pageLabel } from '@/hooks/useFeedbackContext';
import { cropToShot } from './SnipOverlay';
import { MyFeedback } from './MyFeedback';

/* SPEC-506 — the Feedback panel. It renders ONLY after the page picture is taken (phase
 * 'panel'), so it is never in its own screenshot.
 *
 * One tap for the category, one required sentence, and — for Wrong/Broken only — two
 * optional follow-ups. There is no severity rating and no steps-to-reproduce, by design: the
 * one question that matters is "is this stopping you from doing your work today?". */

interface Props {
  flow: FlowState;
  dispatch: (a: FlowAction) => void;
  config: FeedbackConfig | null;
  draft: FeedbackDraft;
  setDraft: React.Dispatch<React.SetStateAction<FeedbackDraft>>;
  tab: 'new' | 'mine';
  setTab: (t: 'new' | 'mine') => void;
  mine: MyReport[];
  unreadIds: string[];
  onOpenMine: () => void;
  onSent: () => void;
  onClose: () => void;
  onSendAnother: () => void;
}

async function fileToShot(file: Blob, maxPx: number, source: Shot['source']): Promise<Shot> {
  const url = URL.createObjectURL(file);
  try {
    const probe = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('not an image'));
      i.src = url;
    });
    return await cropToShot({ url, width: probe.naturalWidth, height: probe.naturalHeight, source }, null, maxPx);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function FeedbackPanel(p: Props) {
  const { flow, dispatch, config, draft, setDraft, tab } = p;
  const where = React.useMemo(() => currentWhere(), []);
  const label = pageLabel(where.page, where.tab);
  const [context, setContext] = React.useState<Record<string, unknown> | null>(null);
  const [showContext, setShowContext] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState<SubmitResult | null>(null);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const textRef = React.useRef<HTMLTextAreaElement>(null);
  const maxPx = config?.max_px ?? 1600;
  // The route templates and known query parameters the context is checked against (config).
  const schema = React.useMemo(
    () => ({ routes: config?.routes ?? [], filterParams: config?.filter_params ?? {}, pages: config?.pages ?? [] }),
    [config]);
  const maxBytes = config?.max_bytes ?? 2 * 1024 * 1024;

  React.useEffect(() => {
    let live = true;
    gatherFeedbackContext().then((c) => { if (live) setContext(c); }).catch(() => { if (live) setContext({}); });
    return () => { live = false; };
  }, []);

  React.useEffect(() => { if (tab === 'mine') p.onOpenMine(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const attach = React.useCallback(async (file: Blob, source: Shot['source']) => {
    if (!file.type.startsWith('image/')) { setError('That file is not an image.'); return; }
    try {
      dispatch({ type: 'set_shot', shot: await fileToShot(file, maxPx, source) });
      setError(null);
    } catch {
      setError('That image could not be read.');
    }
  }, [dispatch, maxPx]);

  // Paste a screenshot (Cmd/Ctrl+V) anywhere while the panel is open.
  React.useEffect(() => {
    if (tab !== 'new' || sent) return;
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((i) => i.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (file) { e.preventDefault(); void attach(file, 'paste'); }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [attach, tab, sent]);

  // Closing with a sentence typed and not yet sent asks first — it would be lost.
  const requestClose = React.useCallback(() => {
    if (!sent && draft.text.trim() && !window.confirm('Discard this report? What you wrote will be lost.')) return;
    p.onClose();
  }, [sent, draft.text, p]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  const send = async () => {
    if (!canSend(draft) || sending) return;
    setSending(true);
    setError(null);
    try {
      let blob: Blob | null = null;
      if (flow.shot) {
        blob = await (await fetch(flow.shot.url)).blob();
        if (blob.size > maxBytes) {
          setError('The screenshot is larger than 2 MB. Remove it, or snip a smaller area.');
          setSending(false);
          return;
        }
      }
      const payload = buildPayload(draft, where, context ?? {}, schema);
      setSent(await submitReport(payload, blob));
      p.onSent();
    } catch (e) {
      setError(e instanceof Error && /^4\d\d/.test(e.message)
        ? e.message.replace(/^\d+:\s*/, '').replace(/^\{"detail":"(.*)"\}$/, '$1')
        : 'Could not send — please try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  const cat = categoryCopy(draft.category);
  // SPEC-521: a category the panel was OPENED with (a scenario idea) stays on offer even after
  // the reader taps another one, so they can switch back.
  const openedWith = React.useRef(draft.category);
  const preview = context ? cleanContext(context, schema) : null;

  return (
    <div data-feedback-ui="" className="fixed inset-0 z-[65]" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
      <div className="absolute inset-0 bg-black/40" onClick={requestClose} />
      <aside className="absolute inset-y-0 right-0 w-full max-w-[440px] bg-[var(--bg-raised)] border-l border-[var(--border-default)] shadow-2xl flex flex-col">
        <header className="flex items-start gap-2 px-4 pt-4 pb-2 border-b border-[var(--border-subtle)]">
          <div className="min-w-0">
            <h2 id="feedback-title" className="text-sm font-semibold text-[var(--text-primary)]">Feedback</h2>
            <p className="text-[11px] text-[var(--text-tertiary)] truncate">{label ?? where.page}</p>
          </div>
          <button type="button" onClick={requestClose} className="ml-auto p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)]" aria-label="Close feedback">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="flex gap-1 px-4 pt-2" role="tablist">
          {(['new', 'mine'] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => p.setTab(t)}
              className={`relative px-3 py-1.5 text-xs rounded-md ${tab === t ? 'bg-[var(--bg-overlay)] text-[var(--text-primary)] font-medium' : 'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'}`}>
              {t === 'new' ? 'New' : 'My feedback'}
              {t === 'mine' && p.unreadIds.length > 0 && <span className="absolute top-1 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-400" />}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {tab === 'mine' ? (
            <MyFeedback items={p.mine} unreadIds={p.unreadIds} onNavigate={p.onClose} />
          ) : sent ? (
            <div className="py-8 text-center space-y-3" role="status">
              <p className="text-sm text-[var(--text-primary)]">{ACK_COPY}</p>
              {flow.shot && !sent.screenshot_saved && (
                <p className="text-xs text-amber-300">Your note was received, but the screenshot could not be stored.</p>
              )}
              <div className="flex justify-center gap-2">
                <button type="button" onClick={p.onSendAnother} className="px-3 py-1.5 text-xs rounded-md border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                  Send another
                </button>
                <button type="button" onClick={() => p.setTab('mine')} className="px-3 py-1.5 text-xs rounded-md bg-amber-500 text-[var(--text-inverse)] font-medium hover:bg-amber-400">
                  Open My feedback
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Screenshot */}
              <section aria-label="Screenshot">
                {flow.shot ? (
                  <div className="space-y-2">
                    <img src={flow.shot.url} alt="The screenshot that will be sent" className="w-full max-h-48 object-contain rounded-md border border-[var(--border-subtle)] bg-[var(--bg-base)]" />
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => dispatch({ type: 'markup' })} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                        <Pencil className="w-3.5 h-3.5" aria-hidden="true" /> Mark it up
                      </button>
                      {config?.auto_capture && (
                        <button type="button" onClick={() => dispatch({ type: 'retake' })} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                          <Camera className="w-3.5 h-3.5" aria-hidden="true" /> Snip again
                        </button>
                      )}
                      <button type="button" onClick={() => dispatch({ type: 'remove_shot' })} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-red-300">
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" /> Remove screenshot
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 rounded-md border border-dashed border-[var(--border-default)] text-center space-y-2">
                    {flow.captureFailed && <p className="text-[11px] text-[var(--text-tertiary)]">The page picture could not be taken this time.</p>}
                    <p className="text-xs text-[var(--text-secondary)]">{PASTE_COPY}</p>
                    <div className="flex justify-center gap-2">
                      <button type="button" onClick={() => fileInput.current?.click()} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                        <ImagePlus className="w-3.5 h-3.5" aria-hidden="true" /> Choose a file
                      </button>
                      {config?.auto_capture && (
                        <button type="button" onClick={() => dispatch({ type: 'retake' })} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                          <Camera className="w-3.5 h-3.5" aria-hidden="true" /> Snip the page
                        </button>
                      )}
                    </div>
                    <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) void attach(f, 'file'); e.target.value = ''; }} />
                  </div>
                )}
              </section>

              {/* Category */}
              <fieldset>
                <legend className="text-xs font-medium text-[var(--text-secondary)] mb-1.5">What is it?</legend>
                <div className="grid grid-cols-2 gap-1.5">
                  {pickerCategories(openedWith.current).map((c) => (
                    <button key={c.key} type="button" aria-pressed={draft.category === c.key}
                      onClick={() => { setDraft((d) => ({ ...d, category: c.key })); setTimeout(() => textRef.current?.focus(), 0); }}
                      className={`text-left px-2.5 py-2 rounded-md border text-xs transition-colors ${draft.category === c.key ? 'border-amber-400 bg-amber-500/10 text-[var(--text-primary)]' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong,var(--border-default))] hover:text-[var(--text-primary)]'}`}>
                      <span className="block font-medium">{c.label}</span>
                      <span className="block text-[10px] text-[var(--text-tertiary)]">{c.hint}</span>
                    </button>
                  ))}
                </div>
              </fieldset>

              {cat && (
                <>
                  <label className="block">
                    <span className="text-xs font-medium text-[var(--text-secondary)]">{cat.prompt}</span>
                    <textarea ref={textRef} value={draft.text} rows={3} maxLength={4000}
                      onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
                      placeholder={examplePrompt(cat.key, label)}
                      className="mt-1 w-full px-2.5 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border-default)] rounded-md text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-1 focus:ring-amber-500/50 resize-y" />
                  </label>
                  {asksFollowUps(cat.key) && (
                    <>
                      <label className="block">
                        <span className="text-xs text-[var(--text-secondary)]">What did you expect to see instead? <span className="text-[var(--text-tertiary)]">(optional)</span></span>
                        <input value={draft.expected} maxLength={4000}
                          onChange={(e) => setDraft((d) => ({ ...d, expected: e.target.value }))}
                          className="mt-1 w-full px-2.5 py-1.5 text-sm bg-[var(--bg-base)] border border-[var(--border-default)] rounded-md text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-amber-500/50" />
                      </label>
                      <div>
                        <span className="text-xs text-[var(--text-secondary)]">Is this stopping you from doing your work today? <span className="text-[var(--text-tertiary)]">(optional)</span></span>
                        <div className="mt-1 flex gap-1.5">
                          {([true, false] as const).map((v) => (
                            <button key={String(v)} type="button" aria-pressed={draft.blocking === v}
                              onClick={() => setDraft((d) => ({ ...d, blocking: d.blocking === v ? null : v }))}
                              className={`px-3 py-1 text-xs rounded-md border ${draft.blocking === v ? 'border-amber-400 bg-amber-500/10 text-[var(--text-primary)]' : 'border-[var(--border-default)] text-[var(--text-secondary)]'}`}>
                              {v ? 'Yes' : 'No'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Technical details — disclosed, viewable, declinable */}
              <section className="text-xs text-[var(--text-tertiary)] space-y-1.5">
                <button type="button" onClick={() => setShowContext((v) => !v)} aria-expanded={showContext}
                  className="inline-flex items-start gap-1 text-left hover:text-[var(--text-secondary)]" disabled={!draft.includeContext}>
                  {showContext ? <ChevronDown className="w-3.5 h-3.5 mt-px shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 mt-px shrink-0" />}
                  <span>{draft.includeContext
                    ? <>We&apos;ll also send: page, filters, data freshness, app version, browser details <span className="underline">View</span></>
                    : 'Only the page you are on, your name and the time will be sent.'}</span>
                </button>
                {showContext && draft.includeContext && (
                  <pre className="max-h-48 overflow-auto p-2 rounded bg-[var(--bg-base)] border border-[var(--border-subtle)] text-[10px] leading-snug whitespace-pre-wrap break-all">
                    {preview ? JSON.stringify(preview, null, 2) : 'Gathering…'}
                  </pre>
                )}
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={!draft.includeContext}
                    onChange={(e) => setDraft((d) => ({ ...d, includeContext: !e.target.checked }))} />
                  Don&apos;t include technical details
                </label>
              </section>

              {error && <p className="text-xs text-red-300" role="alert">{error}</p>}
            </>
          )}
        </div>

        {tab === 'new' && !sent && (
          <footer className="px-4 py-3 border-t border-[var(--border-subtle)]">
            <button type="button" onClick={() => void send()} disabled={!canSend(draft) || sending}
              className="w-full py-2 text-sm font-medium rounded-md bg-amber-500 text-[var(--text-inverse)] hover:bg-amber-400 disabled:opacity-50 transition-colors">
              {sending ? 'Sending…' : 'Send'}
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}
